import React, { useEffect, useState } from 'react';
import TopBar from '@/components/TopBar';
import WeatherCard from '@/components/cards/WeatherCard';
import AQICard from '@/components/cards/AQICard';
import TrafficCard from '@/components/cards/TrafficCard';
import TransitCard from '@/components/cards/TransitCard';
import CurrencyCard from '@/components/cards/CurrencyCard';
import TranslateShortcutCard from '@/components/cards/TranslateShortcutCard';
import AlertsCard from '@/components/cards/AlertsCard';
import NewsCard from '@/components/cards/NewsCard';
import SunCard from '@/components/cards/SunCard';
import FavoritesCard from '@/components/cards/FavoritesCard';
import CostEstimatorCard from '@/components/cards/CostEstimatorCard';
import SafetyScoreCard from '@/components/cards/SafetyScoreCard';
import LiveLocalInfoCard from '@/components/cards/LiveLocalInfoCard';
import ESIMCard from '@/components/cards/ESIMCard';
import HospitalsCard from '@/components/cards/HospitalsCard';
import NeedNowGrid from '@/components/NeedNowGrid';
import InstallPrompt from '@/components/InstallPrompt';
import { useCity } from '@/context/CityContext';
import { getDashboard, getAlerts, getNews, getTransit, isAbortedRequestError } from '@/lib/api';
import { isSessionIntroduced } from '@/lib/session';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, RotateCw } from 'lucide-react';

export default function Home() {
  const { city, hasConsent } = useCity();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [news, setNews] = useState(null);
  const [transit, setTransit] = useState(null);
  const [loadingDash, setLoadingDash] = useState(true);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [loadingNews, setLoadingNews] = useState(true);
  const [loadingTransit, setLoadingTransit] = useState(true);

  // Show intro only on the very first visit (no city ever set).
  // Returning users go straight to Home; the Landing/Hero is reachable via /onboarding.
  useEffect(() => {
    try {
      const everSeen = localStorage.getItem('gt_first_visit_done');
      if (!everSeen && !localStorage.getItem('puvio:city')) {
        navigate('/onboarding');
      } else {
        localStorage.setItem('gt_first_visit_done', '1');
      }
    } catch { /* ignore */ }
  }, [navigate]);

  const refresh = async () => {
    setLoadingDash(true); setLoadingAlerts(true); setLoadingNews(true); setLoadingTransit(true);
    try {
      const [d, a, n, t] = await Promise.allSettled([
        getDashboard(city.lat, city.lon, 'USD', 'EUR,GBP,JPY,INR,AUD,CAD,CNY,HKD,SGD,KRW,THB,AED,MXN,BRL'),
        getAlerts(city.lat, city.lon),
        getNews(city.lat, city.lon),
        getTransit(city.lat, city.lon, 800),
      ]);
      if (d.status === 'fulfilled') setDash(d.value);
      else if (!isAbortedRequestError(d.reason)) toast.error('Dashboard failed');
      if (a.status === 'fulfilled') setAlerts(a.value);
      if (n.status === 'fulfilled') setNews(n.value);
      if (t.status === 'fulfilled') setTransit(t.value); else setTransit(null);
    } finally { setLoadingDash(false); setLoadingAlerts(false); setLoadingNews(false); setLoadingTransit(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [city.lat, city.lon]);

  return (
    <div data-testid="home-screen">
      <TopBar />
      {/* Hero band */}
      <div className="px-5 pt-3 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-bold text-[10.5px] uppercase tracking-[0.24em] text-[#15291C] flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Right now in
            </p>
            <h1 className="font-display text-3xl font-bold tracking-tight leading-tight text-[#0F1A13] mt-1" data-testid="home-city-title">{city.name}</h1>
            {city.country && <p className="text-sm text-[#7A8A80] font-medium mt-0.5">{city.country}</p>}
          </div>
          <button type="button" onClick={refresh} className="w-10 h-10 rounded-full bg-[#0F1A13]/05 border border-[#0F1A13]/10 flex items-center justify-center hover:bg-[#0F1A13]/08 transition-colors gt-focus" aria-label="Refresh" data-testid="home-refresh">
            <RotateCw className={`w-4 h-4 text-[#15291C] ${loadingDash ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="px-5 flex flex-col gap-4 pb-2">
        {/* Top stat row: weather + AQI */}
        <div className="grid grid-cols-2 gap-3">
          <WeatherCard data={dash?.weather} loading={loadingDash} />
          <AQICard data={dash?.aqi} loading={loadingDash} />
        </div>

        <SunCard data={dash?.weather} loading={loadingDash} />
        <LiveLocalInfoCard lat={city.lat} lon={city.lon} />
        <SafetyScoreCard countryCode={city.country_code} cityName={city.name} />
        <CostEstimatorCard countryCode={city.country_code} />
        <ESIMCard countryCode={city.country_code} />
        <TrafficCard data={dash?.traffic} loading={loadingDash} />
        <TransitCard data={transit} loading={loadingTransit} />
        <CurrencyCard defaultFrom="USD" defaultTo="EUR" rates={dash?.currency?.rates} />
        <TranslateShortcutCard />
        <AlertsCard data={alerts} loading={loadingAlerts} />
        <NewsCard data={news} loading={loadingNews} />
        <HospitalsCard lat={city.lat} lon={city.lon} />
        <FavoritesCard />
        <NeedNowGrid />
        <InstallPrompt />
      </div>

      <div className="px-5 py-8 text-center">
        <p className="text-[12px] font-bold text-[#0F1A13] font-display" data-testid="footer-brand">Trivly</p>
        <p className="text-[11px] text-[#7A8A80] mt-1">Plan less. Experience more.</p>
        <div className="mt-4 flex items-center justify-center gap-3 text-[11px] font-semibold text-[#4A5A50]">
          <a href="/legal/privacy" className="hover:text-[#15291C] underline-offset-2 hover:underline" data-testid="footer-privacy">Privacy</a>
          <span aria-hidden="true">·</span>
          <a href="/legal/terms" className="hover:text-[#15291C] underline-offset-2 hover:underline" data-testid="footer-terms">Terms</a>
          <span aria-hidden="true">·</span>
          <a href="https://www.nevika.co" target="_blank" rel="noreferrer" className="hover:text-[#15291C] underline-offset-2 hover:underline" data-testid="footer-nevika">nevika.co</a>
        </div>
        <p className="text-[10px] text-[#A8B5AB] mt-3" data-testid="footer-copyright">
          © {new Date().getFullYear()} Nevika Innovations Pvt Ltd · GST — Applied
        </p>
      </div>
    </div>
  );
}
